import api from "../../../../services/api";

/**
 * Get department list with filters, pagination, and hierarchy support
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.page_size - Items per page (default: 20)
 * @param {string} params.order_by - Field to order by (default: "createdAt")
 * @param {string} params.order_direction - Order direction ASC/DESC (default: "DESC")
 * @param {Object} params.search - Search object with keyword
 * @param {string} params.department_uuid - Optional parent department UUID for hierarchy
 * @returns {Promise<Object>} API response with body and meta
 */
export const getDepartmentList = async (params = {}) => {
  let {
    page = 1,
    page_size = 20,
    order_by = "createdAt",
    order_direction = "DESC",
    search,
    department_uuid,
  } = params;

  if (Array.isArray(order_by)) {
    order_by = order_by[0] || "createdAt";
  }
  if (Array.isArray(order_direction)) {
    order_direction = order_direction[0] || "DESC";
  }

  const validOrderByOptions = ["createdAt", "updatedAt", "name", "status"];
  const orderByStr = String(order_by || "createdAt");
  const validOrderBy = validOrderByOptions.includes(orderByStr)
    ? orderByStr
    : "createdAt";

  let validOrderDirection = "DESC";
  if (order_direction) {
    const directionStr = String(order_direction).toUpperCase();
    if (directionStr === "ASC" || directionStr === "DESC") {
      validOrderDirection = directionStr;
    }
  }

  const requestBody = {
    page: Number(page) || 1,
    page_size: Number(page_size) || 20,
    order_by: validOrderBy,
    order_direction: validOrderDirection,
  };

  if (
    search &&
    search.keyword !== null &&
    search.keyword !== undefined &&
    String(search.keyword).trim().length > 0
  ) {
    requestBody.search = {
      keyword: String(search.keyword).trim(),
    };
  }

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  
  if (department_uuid !== null && department_uuid !== undefined) {
    const trimmedUuid = typeof department_uuid === "string" ? department_uuid.trim() : String(department_uuid).trim();
    
    if (
      trimmedUuid &&
      trimmedUuid !== "" &&
      uuidRegex.test(trimmedUuid) &&
      trimmedUuid !== "optional-uuid" &&
      trimmedUuid !== "null" &&
      trimmedUuid !== "undefined"
    ) {
      requestBody.department_uuid = trimmedUuid;
    }
  }

  try {
    const response = await api.post("/v1/department/list", requestBody);
    const responseData = response.data;

    if (!responseData) {
      throw new Error("Invalid response from server");
    }

    const statusCode =
      responseData.status_code || responseData.statusCode || response?.status || 200;
    const msgCode = responseData.msg_code || responseData.msgCode || [];
    const responseBody = responseData.body || [];
    const meta = responseData.meta || {};

    const hasError =
      responseData.error ||
      (typeof responseData.msgCode === "string" &&
        responseData.msgCode.includes("INVALID")) ||
      (typeof responseData.msgCode === "string" &&
        responseData.msgCode.includes("ERROR")) ||
      responseData.message;

    if (hasError) {
      const errorMessage =
        responseData.message || responseData.error || "API returned an error";
      console.warn(`API error detected: ${errorMessage} (msgCode: ${responseData.msgCode})`);
      throw new Error(errorMessage);
    }

    if (statusCode >= 200 && statusCode < 300) {
      return {
        status_code: statusCode,
        msg_code: msgCode,
        request_id: responseData.request_id || responseData.requestId,
        body: Array.isArray(responseBody) ? responseBody : [],
        meta: meta,
      };
    }

    const errorMessage =
      msgCode.length > 0
        ? msgCode[0].description || msgCode[0].code || "An error occurred while listing departments."
        : "An error occurred while listing departments.";

    const error = new Error(errorMessage);
    error.response = {
      data: responseData,
      status: statusCode,
    };

    throw error;
  } catch (error) {
    console.warn("API call failed, falling back to mock data:", error.message);

    try {
      const mockResponse = await fetch("/mock-department-list.json");
      const mockData = await mockResponse.json();

      let filteredData = [...(mockData.body || [])];

      if (search && search.keyword) {
        const keyword = search.keyword.toLowerCase();
        filteredData = filteredData.filter(
          (department) => {
            const deptName = department.departmentName || '';
            const ownerName = department.ownerName || '';
            const description = department.description || '';
            return (
              deptName.toLowerCase().includes(keyword) ||
              description.toLowerCase().includes(keyword) ||
              ownerName.toLowerCase().includes(keyword)
            );
          }
        );
      }

      if (department_uuid) {
        filteredData = filteredData.filter(
          (department) => department.departmentUuid === department_uuid
        );
      }

      const orderByField = order_by || "createdAt";
      const direction = order_direction.toUpperCase() === "ASC" ? 1 : -1;

      filteredData.sort((a, b) => {
        let valA = a[orderByField];
        let valB = b[orderByField];

        if (orderByField === "createdAt" || orderByField === "updatedAt") {
          valA = new Date(valA);
          valB = new Date(valB);
        } else if (typeof valA === "string") {
          valA = valA.toLowerCase();
          valB = valB?.toLowerCase() || "";
        }

        if (valA < valB) return -1 * direction;
        if (valA > valB) return 1 * direction;
        return 0;
      });

      const totalItems = filteredData.length;
      const startIndex = (page - 1) * page_size;
      const endIndex = startIndex + page_size;
      const paginatedData = filteredData.slice(startIndex, endIndex);

      return {
        status_code: 200,
        msg_code: mockData.msg_code || [
          {
            code: "DEPARTMENT_LIST_RETRIEVED",
            description: "Departments retrieved successfully.",
          },
        ],
        request_id: mockData.request_id || `mock-${Date.now()}`,
        body: paginatedData,
        meta: {
          total_items: totalItems,
          total_pages: Math.ceil(totalItems / page_size),
          current_page: page,
          per_page: page_size,
          has_next: endIndex < totalItems,
          has_previous: startIndex > 0,
        },
      };
    } catch (mockError) {
      console.error("Error fetching mock department data:", mockError);
      return {
        status_code: 500,
        msg_code: [
          {
            code: "DEPARTMENT_LIST_ERROR",
            description: "An unexpected error occurred while listing departments.",
          },
        ],
        body: [],
        meta: null,
      };
    }
  }
};

